import { SERIES_COLORS } from "@volca/ui/tokens/chart-colors";
import { overallMargin } from "./format";
import type { TrendDataset, TrendPeriod } from "./types";

export const ALL = "__all";
/** Departments past the series hues fold into one line. The hues are not cycled. */
export const OTHER = "__other";
/** A margin past either end is a missing figure, not a level the axis should fit. */
const SANE_LOW = -1;
const SANE_HIGH = 1.5;
/**
 * How far past the departments' usual levels a point still belongs on the
 * scale. Further than this, the line stops and the caption names it, so one
 * spike does not flatten every other department.
 */
const REACH = 0.35;

export type Point = { period: string } & Record<string, string | number | null>;

export const num = (point: Point, key: string) => {
  const value = point[key];
  return typeof value === "number" ? value : null;
};

export const sane = (value: number | null): value is number =>
  value !== null && value >= SANE_LOW && value <= SANE_HIGH;

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)] ?? null;
};

/**
 * The latest periods that compare with the latest one, oldest first: the
 * span a change can be measured over. A period run by another calculation
 * (`comparable: false`) ends it, so no change is worked out across it.
 */
export const comparableSpan = (periods: TrendPeriod[]) => {
  if (periods.length === 0) return [];
  let start = periods.length - 1;
  while (start > 0 && periods[start - 1]!.comparable) start -= 1;
  return periods.slice(start);
};

/**
 * The record whose margin moved most over the comparable span, and the
 * period it is measured from. Null with fewer than two comparable periods.
 */
export const moverOf = (periods: TrendPeriod[], dataset: TrendDataset) => {
  const span = comparableSpan(periods);
  const since = span[0];
  if (span.length < 2 || !since) return null;
  const first = new Map(
    (since.segments[dataset.id] ?? []).map((row) => [row.key, row.margin]),
  );
  let mover: { key: string; change: number; since: TrendPeriod } | null = null;
  for (const row of span.at(-1)?.segments[dataset.id] ?? []) {
    const was = first.get(row.key);
    if (was === undefined || !sane(row.margin) || !sane(was)) continue;
    const change = row.margin - was;
    if (!mover || Math.abs(change) > Math.abs(mover.change))
      mover = { key: row.key, change, since };
  }
  return mover;
};

export const marginPoints = (
  periods: TrendPeriod[],
  dataset: TrendDataset,
): Point[] =>
  periods.flatMap((period, index) => {
    const rows = period.segments[dataset.id] ?? [];
    const point: Point = {
      period: period.run_id,
      [ALL]: overallMargin(rows),
      ...Object.fromEntries(rows.map((row) => [row.key, row.margin])),
    };
    return index > 0 && (!period.comparable || !periods[index - 1]!.comparable)
      ? [{ period: `gap:${period.run_id}`, [ALL]: null }, point]
      : [point];
  });

/** Departments with a real margin, in the latest period's order. */
export const readableKeys = (periods: TrendPeriod[], dataset: TrendDataset) => {
  const seen = new Set<string>();
  const keys: string[] = [];
  const add = (key: string, margin: number | null) => {
    if (seen.has(key) || !sane(margin)) return;
    seen.add(key);
    keys.push(key);
  };
  const rows = (period: TrendPeriod) => period.segments[dataset.id] ?? [];
  const latest = periods.at(-1);
  if (latest) {
    for (const row of rows(latest)) add(row.key, row.margin);
  }
  for (const period of periods) {
    for (const row of rows(period)) add(row.key, row.margin);
  }
  return keys;
};

/** Every department named in the series, readable or not. */
export const everyKey = (periods: TrendPeriod[], dataset: TrendDataset) => {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const period of periods) {
    for (const row of period.segments[dataset.id] ?? []) {
      if (seen.has(row.key)) continue;
      seen.add(row.key);
      keys.push(row.key);
    }
  }
  return keys;
};

const seriesValues = (data: Point[], key: string) =>
  data.flatMap((point) => {
    const value = num(point, key);
    return sane(value) ? [value] : [];
  });

/**
 * The axis fits each department's usual level, plus the points near it.
 * A point further out is left off the line (`off`) instead of setting the scale.
 */
export const axisOf = (data: Point[], keys: string[]) => {
  const levels = [ALL, ...keys].flatMap((key) => {
    const mid = median(seriesValues(data, key));
    return mid === null ? [] : [mid];
  });
  if (!levels.length) return { domain: [0, 1] as [number, number], off: [] };
  const nearLow = Math.min(...levels) - REACH;
  const nearHigh = Math.max(...levels) + REACH;
  const shown = [ALL, ...keys].flatMap((key) =>
    seriesValues(data, key).filter(
      (value) => value >= nearLow && value <= nearHigh,
    ),
  );
  const bottom = shown.length ? Math.min(...shown) : nearLow;
  const top = shown.length ? Math.max(...shown) : nearHigh;
  const pad = Math.max(top - bottom, 0.04) * 0.2;
  const domain: [number, number] = [bottom - pad, top + pad];
  const off = keys.filter((key) =>
    data.some((point) => {
      const value = num(point, key);
      return sane(value) && (value < domain[0] || value > domain[1]);
    }),
  );
  return { domain, off };
};

export const colorOf = (index: number) => SERIES_COLORS[index];
